import { FC } from 'react';
import { clsx } from 'clsx';
import { ExpandMoreRounded } from '@mui/icons-material';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './ProjectSelector.scss';
import { ProjectFilterItem, useDropdownState } from '../../../hooks';
import { AddProjectBtn } from '../AddProjectBtn';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration';
import { CustomTooltip } from '../../common';

interface ProjectSelectorProps {
  projects: ProjectFilterItem[];
  selectedProject?: ProjectFilterItem | null;
  onProjectSelect: (projectId: string) => void;
  onProjectAdd?: () => void;
  enableToAdd?: boolean;
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  disableSelectedProjectTooltip?: boolean;
  showChevron?: boolean;
  isDisabledProject?: (project: ProjectFilterItem) => boolean;
  disabledProjectTooltip?: string;
  getDisabledProjectTooltip?: (project: ProjectFilterItem) => string | undefined;
}

const ProjectSelector: FC<ProjectSelectorProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  onProjectAdd,
  enableToAdd = false,
  disabled = false,
  className,
  menuClassName,
  disableSelectedProjectTooltip = false,
  showChevron = false,
  isDisabledProject,
  disabledProjectTooltip,
  getDisabledProjectTooltip,
}) => {
  const { t } = useTranslation();
  const { anchorEl, handleOpenDropdown, handleCloseDropdown } = useDropdownState();

  const handleProjectSelect = (projectId: string) => {
    onProjectSelect(projectId);
    handleCloseDropdown();
  };

  const handleProjectAdd = () => {
    if (onProjectAdd) {
      onProjectAdd();
      handleCloseDropdown();
    }
  };

  return (
    <>
      <div
        className={clsx(
          'project-selector',
          className,
          anchorEl && 'project-selector--open',
          disabled && 'project-selector--disabled'
        )}
        onClick={disabled ? undefined : handleOpenDropdown}
      >
        {selectedProject ? (
          <>
            {selectedProject.appFrom === 'all' ? (
              <span className="project-selector__icon project-selector__icon--all">#</span>
            ) : (
              <img
                src={TODO_INTEGRATION_ICONS[selectedProject.appFrom]}
                alt={selectedProject.appFrom}
                className="project-selector__icon"
              />
            )}
            <CustomTooltip
              title={selectedProject?.name ?? ''}
              placement="top"
              disableHoverListener={disableSelectedProjectTooltip || !selectedProject?.name}
            >
              <Typography variant="body2" className="project-selector__text">
                {selectedProject.name}
              </Typography>
            </CustomTooltip>
          </>
        ) : (
          <Typography
            variant="body2"
            className="project-selector__text project-selector__text--placeholder"
          >
            {t('task.project.selectProject')}
          </Typography>
        )}
        {showChevron && <ExpandMoreRounded className="project-selector__chevron" />}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseDropdown}
        className={clsx('project-selector__dropdown', menuClassName)}
      >
        {projects.map((project) => {
          const itemDisabled = !!isDisabledProject?.(project);
          const tooltip = itemDisabled
            ? (getDisabledProjectTooltip?.(project) ?? disabledProjectTooltip)
            : undefined;
          const menuItem = (
            <MenuItem
              key={project.todoTaskId}
              onClick={itemDisabled ? undefined : () => handleProjectSelect(project.todoTaskId)}
              disabled={itemDisabled}
              className={clsx('project-selector__dropdown__item', {
                'project-selector__dropdown__item--selected':
                  selectedProject?.todoTaskId === project.todoTaskId,
              })}
            >
              {project.appFrom === 'all' ? (
                <span className="project-selector__dropdown__item__icon project-selector__dropdown__item__icon--all">
                  #
                </span>
              ) : (
                <img
                  src={TODO_INTEGRATION_ICONS[project.appFrom]}
                  alt={project.appFrom}
                  className="project-selector__dropdown__item__icon"
                />
              )}
              <Typography variant="body2" className="project-selector__dropdown__item__text">
                {project.name}
              </Typography>
            </MenuItem>
          );

          return tooltip ? (
            <CustomTooltip key={project.todoTaskId} title={tooltip} placement="right">
              <span>{menuItem}</span>
            </CustomTooltip>
          ) : (
            menuItem
          );
        })}
        {enableToAdd && onProjectAdd && (
          <AddProjectBtn
            onClick={handleProjectAdd}
            className="project-selector__dropdown__add-project"
          />
        )}
      </Menu>
    </>
  );
};

export default ProjectSelector;
